import { useEffect, useState } from 'react'

/**
 * Local-only demo overlay state. Lets the presenter toggle bonus
 * features (alert banners, sponsor hotline, custom messages, etc.)
 * on/off live during a NASAA-style demo without polluting the URL
 * or the production data model.
 *
 * Persisted to localStorage so it survives navigation between state
 * pages, and a custom event lets every consumer re-render in sync
 * when any panel writes a change.
 */

export type DemoBranding = 'subtle' | 'cobranded' | 'sponsored'

export type DemoTopic =
  | 'general'
  | 'investment'
  | 'romance'
  | 'tech-support'
  | 'government-impersonation'

export type DemoOverlayState = {
  /** How prominent the sponsor brand should feel. */
  branding: DemoBranding
  /** Show sponsor hotline as a callout in the hero. */
  hotline: boolean
  /** Which scam category to feature. */
  topic: DemoTopic
  /** Show an active scam alert band at the top of the page. */
  alert: boolean
  /** Show a "message from the sponsor" card. */
  message: boolean
  /** Show a constituent-action CTA pill in the hero. */
  cta: boolean
}

export const DEFAULT_DEMO_OVERLAY: DemoOverlayState = {
  branding: 'cobranded',
  hotline: false,
  topic: 'general',
  alert: false,
  message: false,
  cta: false,
}

const STORAGE_KEY = 'sd:demo-overlay'
const EVENT = 'sd:demo-overlay-change'

function readFromStorage(): DemoOverlayState {
  if (typeof window === 'undefined') return DEFAULT_DEMO_OVERLAY
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULT_DEMO_OVERLAY
    const parsed = JSON.parse(raw) as Partial<DemoOverlayState>
    return { ...DEFAULT_DEMO_OVERLAY, ...parsed }
  } catch {
    return DEFAULT_DEMO_OVERLAY
  }
}

function writeToStorage(state: DemoOverlayState) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    window.dispatchEvent(new Event(EVENT))
  } catch {
    // ignore
  }
}

export function useDemoOverlay(): DemoOverlayState {
  const [state, setState] = useState<DemoOverlayState>(DEFAULT_DEMO_OVERLAY)

  useEffect(() => {
    setState(readFromStorage())
    const handler = () => setState(readFromStorage())
    window.addEventListener(EVENT, handler)
    window.addEventListener('storage', handler)
    return () => {
      window.removeEventListener(EVENT, handler)
      window.removeEventListener('storage', handler)
    }
  }, [])

  return state
}

export function updateDemoOverlay(patch: Partial<DemoOverlayState>) {
  const current = readFromStorage()
  writeToStorage({ ...current, ...patch })
}
